/**
 * Calcula el estado del plan basado en billing_day, billing_month, frecuencia y suscriptores activos
 * Prioridad: "Día de cobro" > "Próximo a cobrar" > "Sin montos" > "Active"
 */
export const calculatePlanStatus = (plan) => {
  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth() + 1; // 1-12
  const todayDayOfWeek = today.getDay() || 7; // 0=Domingo → 7, 1-6 = Lunes-Sábado
  const isActive = plan.status === 'Active';
  
  if (!isActive) {
    return plan.status; // Retornar estado original si no está activo
  }
  
  // Si NO tiene billingDay (null o undefined), es "Día de Suscripción" → siempre ACTIVO
  if (!plan.billingDay || plan.billingDay === null) {
    return 'Active';
  }
  
  const billingDay = parseInt(plan.billingDay);
  const billingMonth = plan.billingMonth ? parseInt(plan.billingMonth) : null;
  const frequency = plan.frequency;
  
  // Variables para determinar estado
  let isChargeDay = false;
  let daysUntilCharge = null;
  
  // Calcular según frecuencia
  switch(frequency) {
    case 'Weekly':
      // billingDay es día de la semana (1=Lunes, 7=Domingo)
      isChargeDay = todayDayOfWeek === billingDay;
      
      // Calcular días hasta próximo cobro
      daysUntilCharge = billingDay - todayDayOfWeek;
      if (daysUntilCharge <= 0) {
        daysUntilCharge += 7; // Si ya pasó esta semana, calcular para la próxima
      }
      break;
      
    case 'Monthly':
      isChargeDay = todayDay === billingDay;
      
      // Calcular días hasta próximo cobro
      daysUntilCharge = billingDay - todayDay;
      if (daysUntilCharge < 0) {
        // Si ya pasó este mes, calcular días hasta el próximo mes
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        daysUntilCharge = (daysInMonth - todayDay) + billingDay;
      }
      break;
      
    case 'Quarterly':
      if (billingMonth) {
        // Meses de cobro trimestral: billing_month, +3, +6, +9
        const calculateQuarterlyMonths = (startMonth) => {
          const months = [startMonth];
          for (let i = 1; i < 4; i++) {
            let nextMonth = startMonth + (i * 3);
            while (nextMonth > 12) nextMonth -= 12;
            months.push(nextMonth);
          }
          return months;
        };
        
        const quarterlyMonths = calculateQuarterlyMonths(billingMonth);
        const isChargeMonth = quarterlyMonths.includes(todayMonth);
        isChargeDay = isChargeMonth && todayDay === billingDay;
        
        // Calcular días hasta próximo cobro
        if (isChargeMonth) {
          daysUntilCharge = billingDay - todayDay;
          if (daysUntilCharge < 0) {
            // Ya pasó este trimestre, calcular para el próximo
            daysUntilCharge = null; // No está cerca del cobro
          }
        } else {
          // Calcular días hasta el próximo mes de cobro
          const nextChargeMonth = quarterlyMonths.find(m => {
            if (m > todayMonth) return true;
            if (m === todayMonth && billingDay > todayDay) return true;
            return false;
          }) || quarterlyMonths[0]; // Si no hay próximo este año, tomar el primero del siguiente
          
          if (nextChargeMonth > todayMonth) {
            const monthsUntilCharge = nextChargeMonth - todayMonth;
            // Aproximación: asumir 30 días por mes
            daysUntilCharge = (monthsUntilCharge - 1) * 30 + (new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - todayDay) + billingDay;
          } else {
            // Es para el próximo año
            daysUntilCharge = null;
          }
        }
      }
      break;
      
    case 'Yearly':
      if (billingMonth) {
        const isChargeMonth = todayMonth === billingMonth;
        isChargeDay = isChargeMonth && todayDay === billingDay;
        
        // Calcular días hasta próximo cobro
        if (isChargeMonth) {
          daysUntilCharge = billingDay - todayDay;
          if (daysUntilCharge < 0) {
            // Ya pasó este año
            daysUntilCharge = null;
          }
        } else if (todayMonth < billingMonth) {
          // Calcular días hasta el mes de cobro de este año
          const monthsUntilCharge = billingMonth - todayMonth;
          daysUntilCharge = (monthsUntilCharge - 1) * 30 + (new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - todayDay) + billingDay;
        } else {
          // Ya pasó el mes de cobro este año
          daysUntilCharge = null;
        }
      }
      break;
      
    default:
      // Para frecuencias no reconocidas, usar lógica mensual por defecto
      isChargeDay = todayDay === billingDay;
      daysUntilCharge = billingDay - todayDay;
      if (daysUntilCharge < 0) {
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        daysUntilCharge = (daysInMonth - todayDay) + billingDay;
      }
  }
  
  // 1. DÍA DE COBRO (máxima prioridad)
  if (isChargeDay) {
    return 'ChargeDay';
  }
  
  // 2. SIN MONTOS (para planes Variables sin montos, CON suscriptores activos Y próximo a cobrar)
  if (plan.pricingType === 'Variable' || plan.pricingType === 'dynamic') {
    const hasActiveSubscribers = plan.activeSubscriptionsCount && plan.activeSubscriptionsCount > 0;
    const isNearChargeDate = daysUntilCharge !== null && daysUntilCharge > 0 && daysUntilCharge <= 3;
    
    if (hasActiveSubscribers && isNearChargeDate) {
      // Si nunca se procesó un bulk update, mostrar "Sin montos"
      if (!plan.bulkUpdateLastProcessedAt) {
        return 'NoAmounts';
      }
      
      // Verificar si la última actualización fue hace mucho tiempo
      const lastProcessed = new Date(plan.bulkUpdateLastProcessedAt);
      const daysSinceUpdate = Math.floor((today - lastProcessed) / (1000 * 60 * 60 * 24));
      
      // Si hace más de 7 días que no se actualizan montos, mostrar "Sin montos"
      if (daysSinceUpdate >= 7) {
        return 'NoAmounts';
      }
    }
  }
  
  // 3. PRÓXIMO A COBRAR (3 días antes del cobro)
  // Para planes variables, solo mostrar si tiene suscriptores activos
  if (daysUntilCharge !== null && daysUntilCharge > 0 && daysUntilCharge <= 3) {
    if (plan.pricingType === 'Variable' || plan.pricingType === 'dynamic') {
      const hasActiveSubscribers = plan.activeSubscriptionsCount && plan.activeSubscriptionsCount > 0;
      if (hasActiveSubscribers) {
        return 'NextToCharge';
      }
      // Si es variable sin suscriptores, no mostrar "PRÓXIMO A COBRAR", seguir a ACTIVO
    } else {
      // Para planes Fixed, siempre mostrar PRÓXIMO A COBRAR
      return 'NextToCharge';
    }
  }
  
  // 4. ACTIVO (por defecto)
  return 'Active';
};
